import { useEffect, useRef, useState } from 'react';
import { Marqy } from 'marqy';

import type { SanityAwardsCta } from '#/lib/sanity/queries/awardsCta';
import { cn } from '#/lib/utils';
import { Link } from '#/components/Link';

import { Button } from './Button';
import { Headline } from './Headline';
import { Media } from './Media';

export function AwardsCta(props: SanityAwardsCta) {
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [maxDimensions, setMaxDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // Reset refs array when awards change
  useEffect(() => {
    cardRefs.current = new Array(props.awards.length).fill(null);
    setMaxDimensions(null);
  }, [props.awards.length]);

  // Measure all cards and find the maximum width and height
  useEffect(() => {
    if (cardRefs.current.length === 0) return;

    // Wait for next frame to ensure all cards are rendered
    const timeoutId = setTimeout(() => {
      const dimensions = cardRefs.current
        .filter((ref): ref is HTMLDivElement => ref !== null)
        .map((ref) => ({
          width: ref.offsetWidth,
          height: ref.offsetHeight,
        }));

      if (dimensions.length > 0) {
        const maxWidth = Math.max(...dimensions.map((d) => d.width));
        const maxHeight = Math.max(...dimensions.map((d) => d.height));

        setMaxDimensions({ width: maxWidth, height: maxHeight });
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [props.awards]);

  const hasIntro = Boolean(
    props.headline || props.bodyCopy || props.button?.href,
  );

  return (
    <div className="relative bg-charcoal text-cloud">
      {hasIntro ? (
        <div className="bg-cipher-charcoal-gradient flex items-center flex-col text-center w-full m:pt-100 pt-50 l:pt-160 l:pb-70 pb-30 m:pb-50 relative p-main">
          <div className="fade-in mx-auto max-w-[490px] relative z-10">
            {props.headline ? (
              <Headline
                value={props.headline}
                variant={5}
                highlightClass="text-electric"
                className="mb-12 m:mb-20 "
              />
            ) : null}
            {props.bodyCopy ? (
              <p className="text-body-cloud mb-30 whitespace-pre-wrap m:mb-40">
                {props.bodyCopy}
              </p>
            ) : null}
          </div>
          {props.button?.href ? (
            <Button
              href={props.button.href}
              target={props.button.target}
              variant="primary"
              className="fade-in mt-30 m:mt-0 relative z-10"
            >
              {props.button.title}
            </Button>
          ) : null}
        </div>
      ) : null}

      <div
        className={cn(
          'fade-in relative w-full',
          hasIntro ? 'pb-60 m:pb-100 l:pb-150' : 'py-24 m:py-40',
        )}
      >
        <Marqy>
          <div className="flex items-center gap-x-[var(--gap-x)] pr-[var(--gap-x)] [--gap-x:16px] m:[--gap-x:24px]">
            {props.awards.map((award, index) => {
              const content = (
                <div
                  ref={(el) => {
                    cardRefs.current[index] = el;
                  }}
                  key={award._key}
                  className="flex flex-col items-center justify-center rounded-15 bg-grayscale-06 px-40 pt-40 pb-30 m:rounded-25"
                  style={
                    maxDimensions
                      ? {
                          width: maxDimensions.width,
                          height: maxDimensions.height,
                        }
                      : undefined
                  }
                >
                  <div className="flex items-center justify-center ">
                    <Media
                      media={award.image}
                      className="brightness-0 invert"
                      mediaClassName="object-contain"
                      style={{ width: award.imageWidth }}
                      sizes={`${award.imageWidth}px`}
                    />
                  </div>
                  {award.label && (
                    <p className="mt-15 text-12 leading-150 text-grayscale-03 text-center m:text-14">
                      {award.label}
                    </p>
                  )}
                </div>
              );

              if (award.link?.href) {
                return (
                  <Link
                    key={award._key}
                    href={award.link.href}
                    target={award.link.target}
                    className="group transition-opacity duration-250 hover:opacity-80"
                  >
                    {content}
                  </Link>
                );
              }

              return content;
            })}
          </div>
        </Marqy>
        <div
          className="absolute inset-y-0 left-0 w-[10%] m:w-[15%] xl:w-[20%] pointer-events-none z-10"
          style={{
            background: 'linear-gradient(to right, #1C1C1E, transparent)',
          }}
        ></div>
        <div
          className="absolute inset-y-0 right-0 w-[10%] m:w-[15%] xl:w-[20%] pointer-events-none z-10"
          style={{
            background: 'linear-gradient(to left, #1C1C1E, transparent)',
          }}
        ></div>
      </div>
    </div>
  );
}
